import { Injectable } from "@angular/core";
import { FreightRate } from "../models/freight-rate.model";
import { freightRateGroupSeparator } from "./freight-rate-group-separator.const";
import { FreightRateGroup } from "./freight-rate-group.model";
import { RateLocationMapper } from "../../../rate/rate-location/rate-location.mapper";
import { FclRoute } from "../../../route/route-fcl.model";
import { LclRoute } from "../../../route/route-lcl.model";
import { QuoteLocation } from "../../models/quote-location.model";
import { FclRate } from "../../../rate/rate-fcl.model";
import { ContainerName } from "../../../shared/container/container-name.type.const";
import { FclTier } from "../../../tier/tier-fcl.model";
import { quoteType } from "../../quote-form/quote-type.const";
import { BaseStandardQuote } from "../../models/quote-base-standard.model";
import { locationKind } from "app/location/location-kind.const";

@Injectable()
export class FreightRateGroupService {
  public groupSeparator = freightRateGroupSeparator;

  constructor() {}

  public calculateTiers(
    freightRateGroups: Record<string, FreightRateGroup>,
  ): FclTier {
    const tiers: FclTier[] = [];
    let customFreightRatesCount = 0;
    Object.keys(freightRateGroups).map((key) => {
      const freightRates = freightRateGroups[key].freightRates.filter(
        (freightRate) => freightRate.haveRoute(),
      );
      const customFreightRates = freightRates.filter((freightRate) => {
        const route = freightRate.getRoute();
        if (route instanceof FclRoute) {
          return !route.getContainers().areAllPricesEqual(null);
        }
        if (route instanceof LclRoute) {
          return route.getChargeableWeightPrice() !== null;
        }
      });
      customFreightRatesCount += customFreightRates.length;

      freightRates.map((freightRate) => {
        if (freightRate.route instanceof FclRoute) {
          const tier = freightRate.route.getTier();
          if (tier && tier instanceof FclTier) {
            tiers.push(tier);
          }
        }
      });
    });

    const seenTierIds = new Set<number>();
    const uniqueTiers = tiers.filter((tier) => {
      const id = tier.getId();
      if (seenTierIds.has(id)) {
        return false;
      }
      seenTierIds.add(id);
      return true;
    });

    return !customFreightRatesCount && uniqueTiers.length === 1
      ? uniqueTiers[0]
      : null;
  }

  public groupRates(
    freightRateGroups: Record<string, FreightRateGroup>,
    freightRates: FreightRate[],
  ): Record<string, FreightRateGroup> {
    freightRates.map((freightRate) => {
      const groupName = this.getGroupNameFromRate(freightRate);
      if (
        freightRateGroups[groupName] &&
        freightRateGroups[groupName].freightRates
      ) {
        freightRateGroups[groupName].freightRates.push(freightRate);
      }
    });

    return freightRateGroups;
  }

  public createGroups(
    originLocations: QuoteLocation[],
    destinationLocations: QuoteLocation[],
    quote: BaseStandardQuote,
  ): Record<string, FreightRateGroup> {
    const freightRateGroups = {};

    if (originLocations && destinationLocations) {
      originLocations.map((originLocation) => {
        destinationLocations.map((destinationLocation) => {
          // A port-priced quote groups by Location, so a leg with no port yet has nothing to
          // group — it must not fall through to the address-based branch below.
          if (
            quote.requiresResolvedLocation() &&
            (!originLocation.getLocation() ||
              !destinationLocation.getLocation())
          ) {
            return;
          }

          if (
            originLocation.getLocation() &&
            destinationLocation.getLocation() &&
            originLocation.getLocation().getId() !==
              destinationLocation.getLocation().getId()
          ) {
            const groupName = this.getGroupNameFromLocationPair(
              originLocation.getLocation().getName(),
              destinationLocation.getLocation().getName(),
            );
            const canLoadOnlineRates =
              quote.getKind() !== locationKind.railTerminal;
            const canLoadPartnerOnlineRates =
              quote.getQuoteType() === quoteType.fcl ||
              quote.getQuoteType() === quoteType.lcl ||
              quote.getQuoteType() === quoteType.air;
            freightRateGroups[groupName] = new FreightRateGroup(
              groupName,
              originLocation.getId(),
              destinationLocation.getId(),
              RateLocationMapper.map({
                id: originLocation.getId(),
                location: originLocation.getLocation(),
              }),
              RateLocationMapper.map({
                id: destinationLocation.getId(),
                location: destinationLocation.getLocation(),
              }),
              [],
              null,
              null,
              canLoadOnlineRates,
              [],
              canLoadPartnerOnlineRates,
              false,
              originLocation.getLocation().getKind(),
            );
          } else {
            // Used for LTL
            const groupName = this.getGroupNameFromLocationPair(
              originLocation.getOfficialName(),
              destinationLocation.getOfficialName(),
            );
            freightRateGroups[groupName] = new FreightRateGroup(
              groupName,
              originLocation.getId(),
              destinationLocation.getId(),
              RateLocationMapper.map({}),
              RateLocationMapper.map({}),
              [],
            );
          }
        });
      });
    }

    return freightRateGroups;
  }

  public createGroupNames(
    freightRateGroups: Record<string, FreightRateGroup>,
  ): string[] {
    return Object.keys(freightRateGroups);
  }

  public sortRates(
    freightRateGroups: Record<string, FreightRateGroup>,
    groupName: string,
    fieldName: string,
    order: number,
  ): Record<string, FreightRateGroup> {
    // todo: aargh

    freightRateGroups[groupName].freightRates.sort((totals1, totals2): any => {
      const rate1 = <FclRate>totals1.getRate();
      const rate2 = <FclRate>totals2.getRate();

      switch (fieldName) {
        case "carrier": {
          return order > 0
            ? rate1
                .getCarrier()
                .getName()
                .localeCompare(rate2.getCarrier().getName())
            : rate2
                .getCarrier()
                .getName()
                .localeCompare(rate1.getCarrier().getName());
        }
        case "name": {
          return order > 0
            ? rate1.getName().localeCompare(rate2.getName())
            : rate2.getName().localeCompare(rate1.getName());
        }
        case "validity": {
          const date1 = new Date(rate1.getValidity()[0]);
          const date2 = new Date(rate2.getValidity()[0]);
          return order > 0
            ? date1.getTime() - date2.getTime()
            : date2.getTime() - date1.getTime();
        }
        case "chargeableWeightPrice":
        case "blPrice":
          return order > 0
            ? rate1[fieldName] - rate2[fieldName]
            : rate2[fieldName] - rate1[fieldName];
        default: {
          if (!rate1.getContainers() || !rate2.getContainers()) {
            return -1;
          }

          return order > 0
            ? rate1.getContainers().getPrice(<ContainerName>fieldName) -
                rate2.getContainers().getPrice(<ContainerName>fieldName)
            : rate2.getContainers().getPrice(<ContainerName>fieldName) -
                rate1.getContainers().getPrice(<ContainerName>fieldName);
        }
      }
    });

    return freightRateGroups;
  }

  private getGroupNameFromRate(freightRate: FreightRate): string {
    if (
      freightRate.getRate() &&
      freightRate.getRate().getOriginLocation() &&
      freightRate.getRate().getDestinationLocation()
    ) {
      const groupName =
        freightRate.getRate().getOriginLocation().getLocation().getName() +
        this.groupSeparator +
        freightRate.getRate().getDestinationLocation().getLocation().getName();

      return groupName;
    }
  }

  private getGroupNameFromLocationPair(
    originLocationName: string,
    destinationLocationName: string,
  ): string {
    return originLocationName + this.groupSeparator + destinationLocationName;
  }
}
